import FeatureGates from '@atlaskit/feature-gate-js-client/feature-gates';
import {
	REGISTERED_KEYS,
	getDefinition,
	postGateOverridesToExtensionPage,
	toStorableParameters,
	type ConfigParameters,
	type GateOverrides,
} from '@rovo-extension/feature-gates-adapter';

import { extensionUrl } from '../config';
import { logError } from '../utils/logger';

/**
 * Re-asserts this live client's current overrides to chrome, filtered to registered keys. Called
 * from the same two points `publishGateEnvelope` is, so a one-shot DebugMenu override message that
 * never landed self-heals at the next mount/poll instead of staying silently out of sync.
 */
export function syncGateOverridesToExtension(): void {
	const live = FeatureGates.getOverrides();
	const gates: Record<string, boolean> = {};
	const configs: Record<string, ConfigParameters> = {};

	// Every value is checked rather than cast, because this store is also written by dev tooling and
	// the receiver validates the blob as a whole: one leaf it cannot carry costs every other override
	// in the message. Same reason `overrideConfigParameter` filters, and this path is the wider one,
	// since it re-asserts every registered key on every mount and poll.
	for (const key of REGISTERED_KEYS) {
		const definition = getDefinition(key);
		if (definition?.kind === 'gate') {
			const value = live.gates[key];
			if (typeof value === 'boolean') {
				gates[key] = value;
			}
		} else if (definition && key in live.configs) {
			const storable = toStorableParameters(live.configs[key]);
			// An empty bag would force nothing, so it is not worth a message.
			if (Object.keys(storable).length > 0) {
				configs[key] = storable;
			}
		}
	}

	if (Object.keys(gates).length === 0 && Object.keys(configs).length === 0) {
		return;
	}

	const overrides: GateOverrides = {
		...(Object.keys(gates).length > 0 ? { gates } : {}),
		...(Object.keys(configs).length > 0 ? { configs } : {}),
	};

	try {
		postGateOverridesToExtensionPage(overrides, extensionUrl);
	} catch (error) {
		logError({ error, impact: 'Chrome-layer gate overrides may be out of sync with this session' });
	}
}
